import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useCan } from "../auth/session";
import { Card, DataTable, EmptyState, Field, InlineAlert, PageHeader, PageShell, StatusChip, icons, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import { BandEditor } from "../templates/BandEditor";
import "./builder.css";

/**
 * Program builder (ALN-34, docs/pages/builder.md), laid out as the mockup's builder view: the
 * harvest-to-release card, vertical stage tabs, the stage panel (Process, People, Tools, Pools) and
 * the rail. Every change saves on blur or select and answers with the re-priced program. Plans
 * estimate: nothing here is book value or touches a closed month.
 */
export type Stage = "sourcing" | "farming" | "receiving" | "crush_press" | "fermentation" | "aging" | "blending" | "bottling" | "warehouse";
type Size = "S" | "M" | "L";
type BandUnit = "tons" | "gallons" | "cases" | "acres";
interface Variance { deltaCents: number; pct: number | null; outside: boolean; state: "ok" | "warn" | "bad" }
interface LaborRow {
  id: string; role: string; laborPoolId: string; laborPoolName: string; hours: number | null; rateCents: number | null; cents: number | null; noRate: boolean;
  activityKey: string | null; activityName: string | null; size: Size | null; autoSize: Size | null; sizeSource: string; sizeReason: string | null;
  sizeSetBy: string | null; sizeSetAt: string | null; hoursSource: string; median: number | null; inProcess: boolean;
  /** The size's hours typed in the band editor (templates H13), for "Use the <size> hours". */
  bandHours: number | null;
  band: { unit: BandUnit; smallMax: number; mediumMax: number; version: number; hoursPer: string; volume: number } | null;
}
interface MaterialRow {
  id: string; itemId: string | null; itemName: string | null; qboId: string | null; unit: string | null; description: string; basis: Basis; qtyPerBasis: number;
  totalQty: number; unitCostCents: number; unitCostSource: string; unitCostReason: string | null; unitCostSetBy: string | null; itemCostCents: number | null;
  onlyForSource: string | null; cents: number; skipped: boolean;
}
interface PoolRow { id: string; costPoolId: string; name: string; driver: string | null; driverLabel: string; driverQty: number | null; rateCents: number | null; cents: number | null; noRate: boolean }
interface StageView {
  stage: Stage; used: boolean; present: boolean; status: "planned" | "in_progress" | "complete" | "skipped"; months: string[];
  labor: LaborRow[]; materials: MaterialRow[]; pools: PoolRow[]; laborCents: number; materialCents: number; poolCents: number; totalCents: number;
  railShareCents: number | null; railTotalCents: number; planToDateCents: number; actualCents: number; variance: Variance | null;
}
interface Fields {
  fruitSource: "purchased" | "estate"; plannedTons: number; estateAcres: number | null; estateTonsPerAcre: number | null; farmMonths: number;
  yieldGalPerTon: number; tankDays: number; agingRoute: "none" | "stainless" | "barrel"; agingMonths: number; agingLossPct: number; blendLossPct: number;
  bottlingLossPct: number; warehouseMonths: number; targetCases: number; tolerancePct: number | null;
}
type FieldKey = keyof Fields;
interface Derived {
  tons: number; yieldGalPerTon: number; gallonsPressed: number; gallonsAged: number; gallonsBlended: number; gallonsBottled: number; gallonsPerCase: number; projectedCases: number;
  bottles: number; gallonMonths: number; caseMonths: number; acreMonths: number; barrels: number;
}
export interface BuilderBody {
  program: { id: string; name: string; vintage: number; wineType: string; status: string; planState: string; companyId: string; companyName: string; revision: number; canEdit: boolean; readOnly: boolean };
  /** Set in template mode (`?template=`): the draft template this plan belongs to. Programs have none. */
  template?: TemplateInfo;
  // template mode has no approval, tolerance, true-up or months so far
  fields: Fields; approval: Approval | null; tolerance: { pct: number; isDefault: boolean } | null; derived: Derived; stages: StageView[];
  rail: {
    projectedCases: number; costPerCaseCents: number | null; costPerBottleCents: number | null; totalCents: number | null; notCosted: { id: string; name: string }[];
    hasActuals: boolean; actualCents: number; planToDateCents: number; materialityCents: number; closedThrough: string | null; openMonth: string | null;
  };
  timeline: { phases: { stage: Stage; start: number; end: number; months: number }[]; nowMonth: number; label: string; harvestMonth: number; perMonth: { month: number; cents: number }[]; actualCents: number };
  currentStage: Stage | null;
  options: {
    laborPools: { id: string; name: string }[]; pools: { id: string; name: string; driver: string; driverLabel: string }[];
    items: { id: string; name: string; qboId: string | null; unit: string | null; lastUnitCostCents: number | null; source: string }[]; activities: { key: string; name: string; stage: Stage }[];
  };
  qboConnected: boolean;
  trueUp: TrueUpView | null;
  monthsSoFar: MonthsSoFar | null;
}
interface TemplateInfo { id: string; name: string; status: "draft" | "saved"; origin: string; archived: boolean; pricingCompanyId: string | null; pricingCompanyName: string; harvestMonth: string; canPrice: boolean; showPricedIn: boolean }
/** The harvest true-up (builder B, C; ADR 0013 §9): what was trued up, what is recorded and the plan figures it compares with. */
interface TrueUpView {
  tons: number | null; yieldGalPerTon: number | null; tonsPerAcre: number | null; tonsAt: string | null; yieldAt: string | null; by: string | null; reason: string | null;
  plan: { tons: number; yieldGalPerTon: number; tonsPerAcre: number | null };
  weighTags: { count: number; tons: number } | null; pressedGallons: number | null;
  harvestStarted: boolean; due: { receiving: boolean; crushPress: boolean }; canTrueUp: boolean;
}
/** Months in barrel (or tank) so far (builder H2): derived from the lots, or corrected with a reason. */
interface MonthsSoFar { derived: number; corrected: number | null; reason: string | null; by: string | null; at: string | null; max: number; vessel: "barrel" | "tank" }
type PlanState = "draft" | "submitted" | "approved" | "replan_draft" | "replan_submitted";
interface Approval {
  state: PlanState; submittedBy: { id: string; name: string } | null; submittedAt: string | null; reason: string | null;
  sentBack: { by: string; at: string | null; note: string } | null;
  approved: { version: number; approvedAt: string; approvedBy: string; costPerCaseCents: number; totalCents: number; projectedCases: number; fields: Fields } | null;
  todayCostPerCaseCents: number | null; selfApprovalAllowed: boolean;
  canSubmit: boolean; canApprove: boolean; canSendBack: boolean; canDiscard: boolean; canReprice: boolean;
}
type ApprovalForm = "submit" | "sendBack" | "discard" | null;
interface SizeChange { lineId: string; role: string; from: Size | null; to: Size; volume: number; unit: BandUnit; hand: boolean }
type WriteBody = BuilderBody & { sizeChanges: SizeChange[]; changedAfterSubmission: boolean; lineId?: string; removed?: Record<string, unknown> };
type Basis = "fixed" | "ton" | "gallon" | "case" | "bottle";

const STAGE_NAME: Record<Stage, string> = {
  sourcing: "Sourcing", farming: "Farming", receiving: "Receiving", crush_press: "Crush and press", fermentation: "Fermentation",
  aging: "Aging", blending: "Blending", bottling: "Bottling", warehouse: "Warehouse",
};
const BLURB: Record<Stage, (red: boolean) => string> = {
  sourcing: () => "Grower contracts, vineyard visits and freight for purchased fruit.",
  farming: () => "Estate blocks from bloom to harvest. Used when the program runs on estate fruit.",
  receiving: () => "Weighing, sampling and logging every load. Grape cost enters the winery on the weigh tag.",
  crush_press: red => (red ? "Sort, destem and crush; reds are pressed after fermentation. Yield is set here." : "Whole-cluster or direct press, then settling. Yield is set here."),
  fermentation: red => (red ? "Fermentation on skins with daily cap management, then malolactic." : "Cool fermentation in tank, two to four weeks."),
  aging: () => "Time in tank or barrel. Cost builds every month by gallon-months, so the route and length matter most.",
  blending: () => "Final blend, lab work, fining and filtration. Costs follow the gallons from each lot.",
  bottling: () => "The in-house bottling run. Bulk wine becomes cased goods and the cost per case is set.",
  warehouse: () => "Cased goods held before release.",
};
const STATUS_LABEL = { planned: "Planned", in_progress: "In progress", complete: "Complete", skipped: "Not used" } as const;
const SIZE_NAME: Record<Size, string> = { S: "Small", M: "Medium", L: "Large" };
const WINE: Record<string, string> = { red: "Red", white: "White", rose: "Rosé", sparkling: "Sparkling", dessert: "Dessert", other: "Other" };
const ROUTE: Record<Fields["agingRoute"], string> = { none: "None", stainless: "Stainless", barrel: "Barrel" };
const BASIS_LABEL: Record<Basis, string> = { fixed: "Fixed qty", ton: "Per ton", gallon: "Per gallon", case: "Per case", bottle: "Per bottle" };
const NUMBER = "Enter a number of zero or more.";
const PERCENT = "Enter a number from 0 to 100.";
const SAVE_FAILED = "Not saved. Check your connection and try again.";

const usd = (cents: number, digits = 2) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits });
const usd0 = (cents: number) => usd(cents, Math.abs(cents) % 100 ? 2 : 0);
const rate = (cents: number) => usd(cents, cents < 100 ? 3 : 2);
const num = (n: number, max = 1) => n.toLocaleString("en-US", { maximumFractionDigits: max });
const signed = (cents: number) => `${cents > 0 ? "+" : cents < 0 ? "-" : ""}${usd(Math.abs(cents), 0)}`;
const monthName = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : SAVE_FAILED);
const isNetwork = (e: unknown) => !(e instanceof ApiError) || e.status >= 500;
const tons1 = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** "Save as template" is prefilled with the program name minus the vintage (builder H5). */
const templateNameOf = (name: string, vintage: number) => name.replace(String(vintage), "").replace(/\s+/g, " ").replace(/^[\s,·-]+|[\s,·-]+$/g, "") || name;
const ITEM_UNITS: [string, string][] = [["each", "Each"], ["bottle", "Bottle"], ["case", "Case"], ["gallon", "Gallon"], ["lb", "Pound (lb)"], ["kg", "Kilogram (kg)"], ["ton", "Ton"]];
const widthOf = (pct: number) => ({ width: `${pct}%` });
const leftOf = (pct: number) => ({ left: `${pct}%` });

function bandRange(b: NonNullable<LaborRow["band"]>, size: Size) {
  if (size === "S") return `up to ${num(b.smallMax)} ${b.unit}`;
  if (size === "M") return `${num(b.smallMax)} to ${num(b.mediumMax)} ${b.unit}`;
  return `above ${num(b.mediumMax)} ${b.unit}`;
}
const sizeLine = (r: LaborRow, template: boolean) => (r.band && r.size ? `${SIZE_NAME[r.size]}: ${bandRange(r.band, r.size)}. This ${template ? "template" : "program"}: ${num(r.band.volume)} ${r.band.unit}.` : "");

/* ---------------------------------------------------------------- page */

export function BuilderPage() {
  const canSee = useCan("costs.view");
  const search = new URLSearchParams(useSearch());
  const [, navigate] = useLocation();
  const programId = search.get("program");
  const templateId = search.get("template");
  const templateMode = templateId !== null;
  const stageParam = search.get("stage") as Stage | null;
  const list = useQuery({ queryKey: ["programs", "active", null], queryFn: () => api<{ programs: { id: string; name: string; currentStage: Stage | null }[] }>("/programs?status=active"), enabled: canSee });
  const first = list.data?.programs[0];
  useEffect(() => {
    if (!programId && first && !templateMode) navigate(`/builder?program=${first.id}${first.currentStage ? `&stage=${first.currentStage}` : ""}`, { replace: true });
  }, [programId, first, templateMode, navigate]);

  if (!canSee) {
    return (
      <PageShell>
        <PageHeader title="Program builder" />
        <InlineAlert variant="info" title="You don't have access to programs. Ask an owner or controller." />
      </PageShell>
    );
  }
  // template mode: a draft template from Templates, edited like a program (builder B, Template mode)
  if (templateMode) return <ProgramBuilder key={`t-${templateId}`} id={templateId} template stageParam={stageParam} programs={[]} />;
  if (!programId) {
    if (list.isPending || first) return <PageShell><PageHeader title="Program builder" /><BuilderSkeleton /></PageShell>;
    return (
      <PageShell>
        <PageHeader title="Program builder" />
        <Card><EmptyState title="No program to plan yet. Create one on Programs from a template." action={<Link className="btn" href="/programs">Go to Programs</Link>} /></Card>
      </PageShell>
    );
  }
  return <ProgramBuilder key={programId} id={programId} stageParam={stageParam} programs={list.data?.programs ?? []} />;
}

function ProgramBuilder({ id, stageParam, programs, template = false }: { id: string; stageParam: Stage | null; programs: { id: string; name: string; currentStage: Stage | null }[]; template?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  // a template's plan answers in the builder's shape at /templates/:id/plan (ALN-34 PR 5)
  const base = template ? `/templates/${encodeURIComponent(id)}/plan` : `/programs/${id}`;
  const key = [template ? "template-builder" : "builder", id];
  const q = useQuery({ queryKey: key, queryFn: () => api<BuilderBody>(base), retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2 });
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [conflict, setConflict] = useState<string | null>(null);
  const [moved, setMoved] = useState<SizeChange[]>([]);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const savedTimer = useRef<number>();

  /**
   * One write at a time, each with the revision the last answer gave (builder D). A stale revision is
   * someone else's change: show theirs. Resolves with the answer, or null when it wasn't saved.
   */
  const write = (path: string, method: string, body: Record<string, unknown> | null, opts: { revision?: number } = {}): Promise<WriteBody | null> => {
    const run = queue.current.then(async () => {
      const revision = opts.revision ?? qc.getQueryData<BuilderBody>(key)?.program.revision ?? 0;
      setStatus("saving");
      try {
        const res = method === "DELETE"
          ? await api<WriteBody>(`${base}${path}?revision=${revision}`, { method })
          : await api<WriteBody>(`${base}${path}`, { method, body: { ...body, revision } });
        qc.setQueryData(key, res);
        void qc.invalidateQueries({ queryKey: [template ? "templates" : "programs"] });
        setConflict(null);
        setMoved(res.sizeChanges.length ? res.sizeChanges : []);
        if (res.sizeChanges.length) {
          const n = res.sizeChanges.length;
          toast(`${n} ${n === 1 ? "activity" : "activities"} changed size. ${res.sizeChanges.map(c => `${c.role} is now ${SIZE_NAME[c.to]} (${num(c.volume)} ${c.unit}).`).join(" ")}`);
        }
        if (res.changedAfterSubmission) toast("Changed after submission. Submit it again for approval.");
        setStatus("saved");
        window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setStatus("idle"), 2500);
        return res;
      } catch (e) {
        if (e instanceof ApiError && e.code === "edit_conflict") {
          setConflict(e.message);
          setStatus("idle");
          await qc.invalidateQueries({ queryKey: key });
          return null;
        }
        setStatus(isNetwork(e) ? "failed" : "idle");
        throw e;
      }
    });
    queue.current = run.catch(() => undefined);
    return run;
  };

  /** An approval action (builder C): after any queued edit, posts it and shows the answer. Errors reach the caller. */
  const act = (path: string, body: Record<string, unknown>): Promise<BuilderBody> => {
    const run = queue.current.then(async () => {
      // the revision this page last saw, after any edit queued before the action
      const seen = qc.getQueryData<BuilderBody>(key)?.program.revision;
      const res = await api<BuilderBody>(`/programs/${id}/plan/${path}`, { method: "POST", body: "revision" in body && seen !== undefined ? { ...body, revision: seen } : body });
      qc.setQueryData(key, res);
      void qc.invalidateQueries({ queryKey: ["programs"] });
      setForm(null);
      return res;
    });
    queue.current = run.catch(() => undefined);
    return run;
  };
  /** Runs after every queued edit, with the revision the page then shows, so it never reads a plan mid-save. */
  const afterSaves = <T,>(fn: (revision: number) => Promise<T>): Promise<T> => {
    const run = queue.current.then(() => fn(qc.getQueryData<BuilderBody>(key)?.program.revision ?? 0));
    queue.current = run.catch(() => undefined);
    return run;
  };
  const [form, setForm] = useState<ApprovalForm>(null);
  const [saveTpl, setSaveTpl] = useState(false);

  if (q.isPending) return <PageShell><PageHeader title="Program builder" /><BuilderSkeleton /></PageShell>;
  if (q.isError && template) {
    const gone = q.error instanceof ApiError && q.error.status === 404;
    return (
      <PageShell>
        <PageHeader title="Program builder" breadcrumbs={[{ label: "Templates", href: "/templates" }]} />
        <Card>
          {gone
            ? <EmptyState icon={icons.warning} title="That template isn't here. It may have been deleted." action={<Link className="btn" href="/templates">Go to Templates</Link>} />
            : <EmptyState icon={icons.warning} title="This template couldn't load. Try again in a minute; nothing was changed." action={<button type="button" className="btn" onClick={() => void q.refetch()}>Retry</button>} />}
        </Card>
      </PageShell>
    );
  }
  if (q.isError) {
    const gone = q.error instanceof ApiError && q.error.status === 404;
    return (
      <PageShell>
        <PageHeader title="Program builder" breadcrumbs={[{ label: "Programs", href: "/programs" }]} />
        <Card>
          {gone
            ? <EmptyState icon={icons.warning} title="That program isn't here. It may be in a company you can't see." action={<Link className="btn" href="/programs">Go to Programs</Link>} />
            : <EmptyState icon={icons.warning} title="This program couldn't load. Try again in a minute; nothing was changed." action={<button type="button" className="btn" onClick={() => void q.refetch()}>Retry</button>} />}
        </Card>
      </PageShell>
    );
  }

  const b = q.data;
  const stage = b.stages.find(s => s.stage === stageParam) ?? b.stages.find(s => s.stage === b.currentStage) ?? b.stages[0]!;
  const select = (s: Stage) => navigate(`/builder?${template ? "template" : "program"}=${id}&stage=${s}`, { replace: true });
  const f = b.fields;
  const summary = [
    WINE[b.program.wineType] ?? b.program.wineType,
    f.agingRoute === "none" ? "No aging" : `${ROUTE[f.agingRoute]}, ${num(f.agingMonths)} months`,
    `${f.fruitSource === "estate" ? "Estate" : "Purchased"} fruit`,
    `target ${num(f.targetCases, 0)} cases`,
    b.program.companyName,
  ].filter(Boolean).join(" · ");
  const picker = (
    <span className="bldpick">
      <label className="sr" htmlFor="bldPick">Program</label>
      <select id="bldPick" className="select select--sm" value={id} onChange={e => {
        const p = programs.find(x => x.id === e.target.value);
        if (p) navigate(`/builder?program=${p.id}${p.currentStage ? `&stage=${p.currentStage}` : ""}`);
      }}>
        {!programs.some(p => p.id === id) && <option value={id}>{b.program.name}</option>}
        {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </span>
  );
  const statusText = status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "failed" ? SAVE_FAILED : "Changes save as you go";
  const ctx: Ctx = { b, write, toast, moved, readOnly: b.program.readOnly, template, refresh: () => qc.invalidateQueries({ queryKey: key }) };
  const fail = (e: unknown) => toast(message(e), "error");
  if (b.template) {
    // Save template: after any queued edit, freezes the draft; it then opens read only (templates C)
    const saveTemplate = () => afterSaves(revision => api(`/templates/${encodeURIComponent(id)}/save`, { method: "POST", body: { revision } }))
      .then(async () => { await qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ["templates"] }); toast("Template saved. New programs can start from it."); }, e => {
        // someone changed the draft since this page read it: show theirs before anything is frozen
        if (e instanceof ApiError && e.code === "edit_conflict") void qc.invalidateQueries({ queryKey: key });
        fail(e);
      });
    return <TemplateBuilder b={b} t={b.template} ctx={ctx} stage={stage} select={select} status={status} statusText={statusText} conflict={conflict} onSave={saveTemplate} />;
  }
  const a = b.approval!;
  const submitPlan = () => act("submit", { revision: b.program.revision }).then(() => toast("Sent for approval."), fail);
  const approve = () => act("approve", { revision: b.program.revision }).then(() => toast(a.state === "replan_submitted" ? "Re-plan approved." : "Plan approved. Actual against plan now compares with it."), fail);
  const reprice = () => write("/plan/reprice", "POST", {}).then(r => { if (r) toast("Re-plan started at today's rates. Submit it to make them the baseline."); }, fail);
  const primary = a.canApprove ? <button type="button" className="btn btn--primary" onClick={() => void approve()}>{a.state === "replan_submitted" ? "Approve re-plan" : "Approve plan"}</button>
    : a.canSubmit && a.state === "draft" ? <button type="button" className="btn btn--primary" onClick={() => void submitPlan()}>Submit plan for approval</button>
    : a.canSubmit && a.state === "replan_draft" ? <button type="button" className="btn btn--primary" onClick={() => setForm("submit")}>Submit re-plan</button>
    : null;

  return (
    <PageShell>
      <PageHeader serif title={b.program.name} subtitle={summary} breadcrumbs={[{ label: "Programs", href: "/programs" }, { label: picker }]}
        actions={(!b.program.readOnly || primary) && <>
          {!b.program.readOnly && <span className={`small${status === "failed" ? " bldfail" : " muted"}`} role="status">{statusText}</span>}
          {!b.program.readOnly && <button type="button" className="btn" aria-expanded={saveTpl} aria-controls="bldSaveTpl" onClick={() => setSaveTpl(o => !o)}>Save as template</button>}
          {primary}
        </>} />
      <div className="bld">
        {b.program.status === "cancelled" ? <InlineAlert variant="info" title="This program is cancelled." />
          : b.program.readOnly && <InlineAlert variant="info" title="You can see this plan. Owners, controllers and winemakers change it." />}
        {b.program.status !== "cancelled" && b.approval && <ApprovalCallout b={b} form={form} setForm={setForm} act={act} toast={toast} />}
        {conflict && <InlineAlert variant="warning" title={conflict} />}
        {saveTpl && !b.program.readOnly && <SaveTemplateForm b={b} toast={toast} afterSaves={afterSaves} onClose={() => setSaveTpl(false)} />}
        <TimelineCard b={b} />
        <div className="builder">
          <StageTabs b={b} current={stage.stage} onSelect={select} />
          <StagePanel key={stage.stage} ctx={ctx} s={stage} />
          <Rail b={b} onReprice={a.canReprice ? () => void reprice() : undefined} />
        </div>
      </div>
    </PageShell>
  );
}

/* ---------------------------------------------------------------- approval */

/**
 * The plan's approval callout (builder B: Plan not approved, Re-plan draft, Re-plan waiting, Sent back):
 * who sent it and when, and the secondary actions (Send back, Discard changes) with their forms. The
 * primary action (Submit, Approve) sits in the page header, as in the mockup.
 */
function ApprovalCallout({ b, form, setForm, act, toast }: {
  b: BuilderBody; form: ApprovalForm; setForm: (f: ApprovalForm) => void;
  act: (path: string, body: Record<string, unknown>) => Promise<BuilderBody>; toast: ReturnType<typeof useToast>;
}) {
  const a = b.approval!; // rendered only for programs, which always carry an approval
  const approvedOn = a.approved ? day(a.approved.approvedAt) : "";
  const sent = a.submittedBy && a.submittedAt ? `${a.submittedBy.name} on ${day(a.submittedAt)}` : "";
  const mine = !!a.submittedBy && !a.canApprove && !b.program.readOnly && !a.selfApprovalAllowed;
  const who = "An owner, controller or winemaker approves it. Nobody approves a plan they submitted unless your winery's policy allows it.";
  let title: string;
  let note: string | null = null;
  switch (a.state) {
    case "approved": return null;
    case "draft":
      if (b.program.readOnly && !a.sentBack) return null;
      title = a.sentBack ? `${a.sentBack.by} sent this back${a.sentBack.at ? ` on ${day(a.sentBack.at)}` : ""}: ${a.sentBack.note}`
        : "This plan isn't approved yet. Submit it for approval when the program starts production. Actual against plan compares with the approved plan.";
      if (a.sentBack) note = "The plan stays a draft. Change it and submit it again.";
      break;
    case "submitted":
      title = `Sent for approval by ${sent}.`;
      note = mine ? "Waiting for approval. Someone else has to approve it." : who;
      break;
    case "replan_draft":
      title = a.sentBack ? `${a.sentBack.by} sent this back${a.sentBack.at ? ` on ${day(a.sentBack.at)}` : ""}: ${a.sentBack.note}`
        : `Re-plan draft. These changes aren't the baseline yet. Actual against plan still compares with the plan approved on ${approvedOn}.`;
      note = a.sentBack ? `Re-plan draft. Actual against plan still compares with the plan approved on ${approvedOn}.` : who;
      break;
    case "replan_submitted":
      title = `Re-plan sent for approval by ${sent}: ${a.reason ?? ""}.`;
      note = mine ? "Waiting for approval. Someone else has to approve it." : who;
      break;
  }
  return (
    <div className="bldapproval">
      <InlineAlert variant="info" title={title}>
        {note && <p className="m0">{note}</p>}
        {form === "submit" ? (
          <ReasonForm label="What changed and why?" placeholder="For example: six more tons contracted from the same grower" submit="Submit re-plan"
            required="Say what changed and why." onCancel={() => setForm(null)}
            onSubmit={why => act("submit", { revision: b.program.revision, reason: why }).then(() => toast("Re-plan sent for approval."))} />
        ) : form === "sendBack" ? (
          <ReasonForm label="What needs to change?" placeholder="For example: check the yield against last year" submit="Send back"
            required="Say what needs to change." onCancel={() => setForm(null)}
            onSubmit={why => act("send-back", { note: why }).then(() => toast("Sent back."))} />
        ) : form === "discard" ? (
          <div className="bldconfirm" role="group" aria-label="Discard the re-plan">
            <p className="m0">Discard the re-plan? The plan goes back to the version approved on {approvedOn}.</p>
            <div className="actions">
              <button type="button" className="btn btn--primary" onClick={() => { act("discard", { revision: b.program.revision }).then(() => toast("Re-plan discarded."), e => toast(message(e), "error")); }}>Discard changes</button>
              <button type="button" className="btn" onClick={() => setForm(null)}>Keep editing</button>
            </div>
          </div>
        ) : (a.canSendBack || a.canDiscard) && (
          <div className="actions bldapproval__a">
            {a.canSendBack && <button type="button" className="btn" onClick={() => setForm("sendBack")}>Send back</button>}
            {a.canDiscard && <button type="button" className="btn" onClick={() => setForm("discard")}>Discard changes</button>}
          </div>
        )}
      </InlineAlert>
    </div>
  );
}

/** A dot on a field a re-plan changed, with the approved value (builder B, Re-plan draft). */
function ChangedDot({ approved }: { approved: string }) {
  return <span className="bldchg" role="img" aria-label={`Changed. Approved: ${approved}`} title={`Approved: ${approved}`} />;
}

interface Ctx {
  b: BuilderBody;
  write: (path: string, method: string, body: Record<string, unknown> | null, opts?: { revision?: number }) => Promise<WriteBody | null>;
  toast: ReturnType<typeof useToast>;
  moved: SizeChange[];
  readOnly: boolean;
  /** Template mode: words say template, and nothing harvest-only shows. */
  template: boolean;
  /** Template mode: opens the band editor for an activity ("Edit sizes"). */
  editSizes?: (activityKey: string) => void;
  /** Reads the program again, after a write outside it (an item added by hand). */
  refresh: () => Promise<unknown>;
}

/* ---------------------------------------------------------------- timeline */

function TimelineCard({ b }: { b: BuilderBody }) {
  const t = b.timeline;
  if (!t.phases.length) return null;
  const start = Math.min(...t.phases.map(p => p.start));
  const end = Math.max(...t.phases.map(p => p.end)) + 1;
  const total = end - start;
  const W = 1000, top = 14, base = 74;
  const x = (m: number) => ((m - start) / total) * W;
  const byMonth = new Map(t.perMonth.map(m => [m.month, m.cents]));
  const sum = t.perMonth.reduce((a, m) => a + m.cents, 0) || 1;
  let run = 0;
  const pts = [`0,${base}`];
  for (let m = start; m < end; m++) { run += byMonth.get(m) ?? 0; pts.push(`${x(m + 1).toFixed(1)},${(base - (run / sum) * (base - top)).toFixed(1)}`); }
  // template mode has no Today marker
  const now = !b.template && t.nowMonth >= start && t.nowMonth < end ? t.nowMonth - start + 0.5 : null;
  const step = total > 18 ? 3 : total > 8 ? 2 : 1;
  const ticks: number[] = [];
  for (let m = 0; m <= total; m += step) ticks.push(m);
  const today = now !== null && b.rail.hasActuals;
  const at = (m: number) => ({ left: `${(m / total) * 100}%` });
  return (
    <Card title={<>Harvest to release <Tip term="Harvest to release" /></>} headingId="bld-tl"
      description={`${num(total, 0)} months. The line is planned cost building up month by month${today ? `; the marker is where the wine is today, with ${usd0(b.rail.actualCents)} spent so far` : ""}.`}>
      <div className="card__c">
        <div className="tl">
          <svg viewBox={`0 0 ${W} 100`} preserveAspectRatio="none" role="img"
            aria-label={`Timeline: ${t.phases.map(p => `${STAGE_NAME[p.stage]} ${p.months} ${p.months === 1 ? "month" : "months"}`).join(", ")}. Planned cost ${b.rail.totalCents === null ? "not costed yet" : usd0(b.rail.totalCents)}.`}>
            {t.phases.map(p => <rect key={p.stage} className={`tl__seg tl__seg--${p.stage}`} x={x(p.start)} y={82} width={Math.max(2, x(p.end + 1) - x(p.start) - 3)} height={14} />)}
            <polyline className="tl__line" points={pts.join(" ")} fill="none" />
            {now !== null && <line className="tl__now" x1={x(start + now)} x2={x(start + now)} y1={4} y2={98} />}
          </svg>
          {now !== null && <span className="tl__nowlbl small" style={at(now)}>Today</span>}
          {b.rail.totalCents !== null && <span className="tl__cost small tnum">{usd0(b.rail.totalCents)}</span>}
        </div>
        <div className="tl__ticks small muted" aria-hidden="true">
          {ticks.map(m => <span key={m} style={at(m)}>{m === 0 ? "Harvest" : m}</span>)}
        </div>
        <div className="tl__legend small muted">
          {t.phases.map(p => <span key={p.stage}><i className={`tl__key tl__seg--${p.stage}`} />{STAGE_NAME[p.stage]} · {p.months} mo</span>)}
        </div>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- stage tabs */

function flagOf(v: Variance | null) {
  if (!v || (v.state !== "warn" && v.state !== "bad")) return null;
  const shown = v.pct === null ? "none planned" : `${v.pct > 0 ? "+" : "−"}${num(Math.abs(v.pct), 0)}%`;
  const spoken = v.pct === null ? `${usd(Math.abs(v.deltaCents), 0)} spent with nothing planned to date` : v.pct > 0 ? `${num(v.pct, 0)}% against plan` : `${num(Math.abs(v.pct), 0)}% under plan`;
  return <span className={`flag flag--${v.state}`}><span aria-hidden="true">{shown}</span><span className="sr">, {spoken}</span></span>;
}

function StageTabs({ b, current, onSelect }: { b: BuilderBody; current: Stage; onSelect: (s: Stage) => void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = b.stages.findIndex(s => s.stage === current);
    const to = e.key === "ArrowDown" || e.key === "ArrowRight" ? (i + 1) % b.stages.length
      : e.key === "ArrowUp" || e.key === "ArrowLeft" ? (i - 1 + b.stages.length) % b.stages.length
      : e.key === "Home" ? 0 : e.key === "End" ? b.stages.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    onSelect(b.stages[to]!.stage);
    refs.current[to]?.focus();
  };
  return (
    <div className="stagetabs" role="tablist" aria-label="Program stages" aria-orientation="vertical" onKeyDown={onKey}>
      {b.stages.map((s, i) => {
        const done = s.status === "complete";
        return (
          <button key={s.stage} ref={el => { refs.current[i] = el; }} type="button" role="tab" id={`tab-${s.stage}`} aria-selected={s.stage === current} aria-controls="stagePanel"
            tabIndex={s.stage === current ? 0 : -1} onClick={() => onSelect(s.stage)}>
            <span className={`n n--${s.used ? s.status : "off"}`} aria-hidden="true">{done ? "✓" : i + 1}</span>
            <span className="lbl"><b>{STAGE_NAME[s.stage]}</b><span>{STATUS_LABEL[s.used ? s.status : "skipped"]}</span></span>
            <span className="amt tnum">
              <span>{s.used && s.railShareCents !== null ? usd(s.railShareCents) : "—"}</span>
              {s.used && flagOf(s.variance)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- stage panel */

function StageVariance({ s }: { s: StageView }) {
  const v = s.variance;
  if (!v) return null;
  const tone = v.state === "bad" ? "destructive" : v.state === "warn" ? "warning" : "success";
  const shown = v.pct === null ? `${signed(v.deltaCents)} · none planned` : `${v.pct > 0 ? "+" : ""}${num(v.pct)}% · ${signed(v.deltaCents)}`;
  const spoken = v.pct === null ? `${usd(Math.abs(v.deltaCents), 0)} spent with nothing planned to date`
    : `${num(Math.abs(v.pct))}% ${v.deltaCents >= 0 ? "over" : "under"} plan`;
  return (
    <StatusChip tone={tone} quiet={v.state === "ok"}>
      {v.state === "ok" ? icons.check : icons.warning}<span aria-hidden="true">{shown}</span><span className="sr">{spoken}</span>
    </StatusChip>
  );
}

function StagePanel({ ctx, s }: { ctx: Ctx; s: StageView }) {
  const { b } = ctx;
  const idx = b.stages.indexOf(s);
  const red = b.program.wineType === "red";
  const farmingOff = s.stage === "farming" && b.fields.fruitSource !== "estate";
  const agingOff = s.stage === "aging" && b.fields.agingRoute === "none";
  const showActual = s.used && b.rail.closedThrough !== null && (s.actualCents !== 0 || s.planToDateCents > 0);
  // True up harvest sits in the stage head of Receiving and Crush and press once the stage has started (builder B, Decided Oct 18 2026)
  const [trueUpOpen, setTrueUpOpen] = useState(false);
  const harvestStage = s.stage === "receiving" || s.stage === "crush_press";
  const trued = !!(b.trueUp?.tonsAt || b.trueUp?.yieldAt);
  const trueUpButton = harvestStage && b.trueUp?.canTrueUp && s.used && s.status !== "planned" && !ctx.readOnly
    ? <button type="button" className="btn btn--sm" aria-expanded={trueUpOpen} aria-controls="bldTrueUp" onClick={() => setTrueUpOpen(o => !o)}>{trued ? "Correct true-up" : "True up harvest"}</button>
    : null;
  const toEstate = async () => {
    try {
      const res = await ctx.write("", "PATCH", { changes: { fruitSource: "estate" } });
      if (res) ctx.toast("Switched to estate fruit. Farming costs are in; grape purchases are out.");
    } catch (e) { ctx.toast(message(e), "error"); }
  };
  return (
    <section className="ppt" id="stagePanel" role="tabpanel" aria-labelledby={`tab-${s.stage}`}>
      <div className="stagehead">
        <div>
          <p className="eyebrow">Stage {idx + 1} of {b.stages.length} · {s.used ? usd0(s.totalCents) : "not used"}</p>
          <h2 id="stageTitle">{STAGE_NAME[s.stage]}</h2>
          <p className="pagesub">{BLURB[s.stage](red)}</p>
        </div>
        {(showActual || trueUpButton) && (
          <div className="stagehead__a">
            {showActual && <StageVariance s={s} />}
            {showActual && <span className="small muted tnum">Actual {usd0(s.actualCents)} against {usd0(s.planToDateCents)} planned to date</span>}
            {trueUpButton}
          </div>
        )}
      </div>
      {harvestStage && s.used && b.trueUp && <HarvestNote ctx={ctx} s={s} open={trueUpOpen} onClose={() => setTrueUpOpen(false)} />}
      {farmingOff ? (
        <Card>
          <EmptyState title={`This ${ctx.template ? "template" : "program"} runs on purchased fruit`}
            description="Farming costs only apply to estate fruit. Switch the source to Estate and this stage fills from the farming team's pools, while grape purchases drop out of Sourcing."
            action={!ctx.readOnly && <button type="button" className="btn btn--primary" onClick={() => void toEstate()}>Switch to estate fruit</button>} />
        </Card>
      ) : (
        <>
          {agingOff && <Card><EmptyState title={`This ${ctx.template ? "template" : "program"} has no aging`} description="Its route is None, so nothing is planned here. Choose Stainless or Barrel in the process quantities to plan aging." /></Card>}
          <ProcessCard ctx={ctx} s={s} />
          {!agingOff && (
            <>
              <PeopleCard ctx={ctx} s={s} />
              <ToolsCard ctx={ctx} s={s} />
              <PoolsCard ctx={ctx} s={s} />
            </>
          )}
        </>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- harvest true-up */

/**
 * Under the stage head of Receiving and Crush and press (builder B): the true-up form when it's open,
 * what was trued up and the plan it compares with, and the due reminder. The reminder has no button of
 * its own; True up harvest is one control, in the stage head.
 */
function HarvestNote({ ctx, s, open, onClose }: { ctx: Ctx; s: StageView; open: boolean; onClose: () => void }) {
  const tu = ctx.b.trueUp!; // shown only when the program has a true-up (never in template mode)
  if (open) return <TrueUpForm ctx={ctx} onClose={onClose} />;
  const at = tu.tonsAt ?? tu.yieldAt;
  const due = s.stage === "receiving" ? tu.due.receiving : tu.due.crushPress;
  const estate = ctx.b.fields.fruitSource === "estate";
  const parts = [
    tu.tons !== null ? `${tons1(tu.tons)} tons (plan ${tons1(tu.plan.tons)})` : null,
    tu.yieldGalPerTon !== null ? `${num(tu.yieldGalPerTon, 2)} gal/ton (plan ${num(tu.plan.yieldGalPerTon, 2)})` : null,
  ].filter(Boolean);
  return (
    <>
      {at && (
        <p className="small muted m0 bldtrued">
          Trued up {day(at)}{tu.by ? ` by ${tu.by}` : ""}: {parts.join(", ")}.
          {estate && tu.tonsPerAcre !== null && tu.plan.tonsPerAcre !== null && ` ${num(tu.tonsPerAcre, 1)} tons per acre (plan ${num(tu.plan.tonsPerAcre, 1)}).`}
        </p>
      )}
      {due && <InlineAlert variant="warning" title={s.stage === "receiving" ? "Harvest is in. True up tons so the plan follows the fruit you got." : "Pressing is done. True up the yield."} />}
    </>
  );
}

/** True up harvest, or Correct true-up once trued up (builder C; ADR 0013 §9). */
function TrueUpForm({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const tu = ctx.b.trueUp!; // shown only when the program has a true-up (never in template mode)
  const correcting = !!(tu.tonsAt || tu.yieldAt);
  const pressedYield = (t: number) => (tu.pressedGallons !== null && t > 0 ? Math.round((tu.pressedGallons / t) * 100) / 100 : null);
  const startTons = correcting && tu.tons !== null ? tu.tons : tu.weighTags ? tu.weighTags.tons : null;
  const startYield = correcting && tu.yieldGalPerTon !== null ? tu.yieldGalPerTon : pressedYield(startTons ?? 0);
  const [tons, setTons] = useState(startTons === null ? "" : String(startTons));
  const [yieldGal, setYieldGal] = useState(startYield === null ? "" : String(startYield));
  const [why, setWhy] = useState("");
  const [errors, setErrors] = useState<{ tons?: string; yield?: string; why?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async () => {
    const t = tons.trim(), y = yieldGal.trim();
    const tn = Number(t), yn = Number(y);
    const next: typeof errors = {};
    if ((t && !(tn > 0)) || (!t && !y)) next.tons = "Enter the tons harvested, above zero.";
    if (y && !(yn > 0)) next.yield = "Enter gallons per ton, above zero.";
    // a figure that isn't the recorded one is typed, and says where it comes from
    const typed = (t && tn !== tu.weighTags?.tons) || (y && yn !== pressedYield(t ? tn : tu.tons ?? 0));
    if (typed && !why.trim()) next.why = "Say where these figures come from.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const res = await ctx.write("/true-up", "POST", { ...(t ? { tons: tn } : {}), ...(y ? { yieldGalPerTon: yn } : {}), ...(why.trim() ? { reason: why.trim() } : {}) });
      if (res) { ctx.toast(`Trued up. Tons ${num(res.derived.tons, 3)}, yield ${num(res.derived.yieldGalPerTon, 2)} gal/ton.`); onClose(); }
    } catch (e) { setErrors({ form: message(e) }); } finally { setBusy(false); }
  };
  const description = correcting
    ? `Trued up ${day((tu.tonsAt ?? tu.yieldAt)!)}${tu.by ? ` by ${tu.by}` : ""}: ${tu.tons !== null ? `${tons1(tu.tons)} tons` : "planned tons"}, ${tu.yieldGalPerTon !== null ? `${num(tu.yieldGalPerTon, 2)} gal/ton` : "planned yield"}. Saving replaces those figures. It changes plan quantities only; book cost stays actual cost.`
    : "Replace planned tons and yield with what the harvest gave. It changes plan quantities only; book cost stays actual cost.";
  return (
    <div id="bldTrueUp">
      <Card title={correcting ? "Correct true-up" : "True up harvest"} headingId="bldTrueUpT" description={description}>
        <form className="card__c fields bldtrueup" noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
          <Field label="Tons harvested" error={errors.tons}
            hint={correcting ? `Plan ${tons1(tu.plan.tons)}.` : tu.weighTags ? `From ${tu.weighTags.count} weigh ${tu.weighTags.count === 1 ? "tag" : "tags"}.` : `No weigh tags recorded. Plan ${tons1(tu.plan.tons)}.`}>
            <input ref={first} className="input num" type="text" inputMode="decimal" value={tons} onChange={e => setTons(e.target.value)} />
          </Field>
          <Field label="Gallons per ton" error={errors.yield}
            hint={correcting ? `Plan ${num(tu.plan.yieldGalPerTon, 2)}.` : tu.pressedGallons !== null ? `From ${num(tu.pressedGallons, 0)} gallons pressed.` : `No pressed volume recorded yet. Plan ${num(tu.plan.yieldGalPerTon, 2)}.`}>
            <input className="input num" type="text" inputMode="decimal" value={yieldGal} onChange={e => setYieldGal(e.target.value)} />
          </Field>
          <Field label="Where these figures come from" error={errors.why} className="bldspan">
            <input className="input" maxLength={500} value={why} onChange={e => setWhy(e.target.value)}
              placeholder={correcting ? "For example: the press log was read wrong; tank T-7 regauged" : "For example: press log, tank T-7 gauged after pressing"} />
          </Field>
          <div className="actions bldspan">
            <button type="submit" className="btn btn--primary" disabled={busy}>{correcting ? "Save correction" : "Save true-up"}</button>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
          </div>
          {errors.form && <div className="bldspan"><InlineAlert variant="error" title={errors.form} /></div>}
        </form>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- months so far */

/** Months in barrel (or tank) so far (builder B, C, H2): read only from the lots, with an audited correction. */
function MonthsSoFarBlock({ ctx, m }: { ctx: Ctx; m: MonthsSoFar }) {
  const [open, setOpen] = useState(false);
  const fail = (e: unknown) => ctx.toast(message(e), "error");
  const clear = () => ctx.write("/months-so-far", "DELETE", null).then(r => { if (r) ctx.toast("Using the figure from the lots."); }, fail);
  return (
    <>
      <div className="derived bldmonths">
        <span>Months in {m.vessel} so far <Tip term="Months so far" /></span>
        <b className="tnum">{num(m.corrected ?? m.derived)}</b>
        {m.corrected !== null
          ? <small className="muted">Corrected by {m.by ?? "someone"}{m.at ? ` on ${day(m.at)}` : ""}: {m.reason}</small>
          : <small className="muted">From the lots and posted closes</small>}
        {!ctx.readOnly && (
          <span className="bldmonths__a">
            {m.corrected !== null && <button type="button" className="linkbtn small" onClick={() => void clear()}>Use the figure from the lots</button>}
            <button type="button" className="linkbtn small" aria-expanded={open} aria-controls="bldMonthsForm" onClick={() => setOpen(o => !o)}>Correct</button>
          </span>
        )}
      </div>
      {open && <MonthsForm ctx={ctx} m={m} onClose={() => setOpen(false)} />}
    </>
  );
}

function MonthsForm({ ctx, m, onClose }: { ctx: Ctx; m: MonthsSoFar; onClose: () => void }) {
  const [months, setMonths] = useState(String(m.corrected ?? m.derived));
  const [why, setWhy] = useState("");
  const [errors, setErrors] = useState<{ months?: string; why?: string; form?: string }>({});
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async () => {
    const n = Number(months.trim());
    const next: typeof errors = {};
    if (months.trim() === "" || !Number.isFinite(n) || n < 0 || n > m.max) next.months = `Enter a number from 0 to ${num(m.max)}.`;
    if (!why.trim()) next.why = "Say why you're correcting it.";
    setErrors(next);
    if (Object.keys(next).length) return;
    try {
      const res = await ctx.write("/months-so-far", "PUT", { months: n, reason: why.trim() });
      if (res) { ctx.toast("Months so far corrected."); onClose(); }
    } catch (e) { setErrors({ form: message(e) }); }
  };
  return (
    <form id="bldMonthsForm" className="bldreason bldspan" noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
      <Field label={`Months in ${m.vessel} so far`} error={errors.months}>
        <input ref={first} className="input num w-qty" type="text" inputMode="decimal" value={months} onChange={e => setMonths(e.target.value)} />
      </Field>
      <Field label="Why the lots are wrong" error={errors.why} className="bldreason__f">
        <input className="input" maxLength={500} value={why} placeholder="For example: the wine went into barrel two weeks before the lot record shows"
          onChange={e => setWhy(e.target.value)} onKeyDown={e => { if (e.key === "Escape") onClose(); }} />
      </Field>
      <div className="actions">
        <button type="submit" className="btn btn--primary">Save correction</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
      </div>
      {errors.form && <InlineAlert variant="error" title={errors.form} />}
    </form>
  );
}

/* ---------------------------------------------------------------- save as template */

/** Save as template (builder C, H5): asks for a name, prefilled with the program name minus the vintage. */
function SaveTemplateForm({ b, toast, afterSaves, onClose }: { b: BuilderBody; toast: ReturnType<typeof useToast>; afterSaves: <T>(fn: (revision: number) => Promise<T>) => Promise<T>; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(templateNameOf(b.program.name, b.program.vintage));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.select(); }, []);
  const submit = async () => {
    const v = name.trim();
    if (!v) { setError("Give the template a name."); input.current?.focus(); return; }
    setBusy(true);
    try {
      // after any edit still saving, and only from the plan this page shows
      await afterSaves(revision => api(`/templates`, { method: "POST", body: { programId: b.program.id, name: v, revision } }));
      void qc.invalidateQueries({ queryKey: ["templates"] });
      toast(`Saved "${v}" as a template.`);
      onClose();
    } catch (e) { setError(message(e)); input.current?.focus(); } finally { setBusy(false); }
  };
  return (
    <div id="bldSaveTpl">
      <Card title="Save as template" headingId="bldSaveTplT"
        description="Saves this plan for the next vintage: its quantities, rows, sizes and hours, without actuals or rate overrides. It's priced in this program's company as if harvested at the next harvest.">
        <form className="card__c bldreason" noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
          <Field label="Template name" error={error} className="bldreason__f">
            <input ref={input} className="input" maxLength={80} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Escape") onClose(); }} />
          </Field>
          <div className="actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>Save template</button>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- item added by hand */

/** Add an item by hand (builder C, H4): only when the company has no live QuickBooks connection. */
function HandItemForm({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("");
  const [cost, setCost] = useState("");
  const [why, setWhy] = useState("");
  const [errors, setErrors] = useState<{ name?: string; unit?: string; cost?: string; why?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async () => {
    const dollars = Number(cost.trim());
    const next: typeof errors = {};
    if (!name.trim()) next.name = "Give the item a name.";
    if (!unit) next.unit = "Choose a unit.";
    if (cost.trim() === "" || !Number.isFinite(dollars) || dollars < 0) next.cost = NUMBER;
    if (!why.trim()) next.why = "Say why it isn't in the list.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await api(`/items`, { method: "POST", body: { companyId: ctx.b.program.companyId, name: name.trim(), unit, unitCostCents: Math.round(dollars * 100), reason: why.trim() } });
      await ctx.refresh();
      ctx.toast(`Added "${name.trim()}" to the item list.`);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "name_taken") setErrors({ name: e.message });
      else setErrors({ form: message(e) });
    } finally { setBusy(false); }
  };
  return (
    <form className="fields bldhanditem" noValidate aria-label="Add an item by hand" onSubmit={e => { e.preventDefault(); void submit(); }}>
      <Field label="Item name" error={errors.name}>
        <input ref={first} className="input" maxLength={120} value={name} onChange={e => setName(e.target.value)} />
      </Field>
      <Field label="Unit" error={errors.unit}>
        <select className="select" value={unit} onChange={e => setUnit(e.target.value)}>
          <option value="">Choose a unit</option>
          {ITEM_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="Unit cost ($)" error={errors.cost}>
        <input className="input num" type="text" inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} />
      </Field>
      <Field label="Why it isn't in the list" error={errors.why} className="bldspan">
        <input className="input" maxLength={500} value={why} placeholder="For example: a new closure for the reserve tier" onChange={e => setWhy(e.target.value)} />
      </Field>
      <div className="actions bldspan">
        <button type="submit" className="btn btn--primary" disabled={busy}>Add item</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
      </div>
      {errors.form && <div className="bldspan"><InlineAlert variant="error" title={errors.form} /></div>}
    </form>
  );
}

/* ---------------------------------------------------------------- process */

type FieldDef = { key: FieldKey; label: string; kind: "num" | "pct" | "int" | "source" | "route"; tip?: string; onlyIf?: "purchased" };
const STAGE_FIELDS: Record<Stage, FieldDef[]> = {
  sourcing: [
    { key: "fruitSource", label: "Fruit source", kind: "source" },
    { key: "plannedTons", label: "Tons contracted", kind: "num", onlyIf: "purchased" },
    { key: "tolerancePct", label: "Cost tolerance (±%)", kind: "pct", tip: "Cost tolerance (±%)" },
  ],
  farming: [
    { key: "estateAcres", label: "Estate acres in this program", kind: "num" },
    { key: "estateTonsPerAcre", label: "Expected tons per acre", kind: "num" },
    { key: "farmMonths", label: "Months farmed (bloom to harvest)", kind: "num" },
  ],
  receiving: [],
  crush_press: [{ key: "yieldGalPerTon", label: "Yield (gallons per ton)", kind: "num" }],
  fermentation: [{ key: "tankDays", label: "Tank-days", kind: "num" }],
  aging: [
    { key: "agingRoute", label: "Aging route", kind: "route", tip: "Aging route" },
    { key: "agingMonths", label: "Months", kind: "num" },
    { key: "agingLossPct", label: "Loss to evaporation and topping (%)", kind: "pct", tip: "Loss to evaporation and topping (%)" },
  ],
  blending: [{ key: "blendLossPct", label: "Loss in blending and filtration (%)", kind: "pct" }],
  bottling: [
    { key: "targetCases", label: "Target cases", kind: "int" },
    { key: "bottlingLossPct", label: "Bottling loss (%)", kind: "pct" },
  ],
  warehouse: [{ key: "warehouseMonths", label: "Months held before release", kind: "num" }],
};

function derivedOf(b: BuilderBody, stage: Stage): [string, string, string?][] {
  const d = b.derived, f = b.fields;
  switch (stage) {
    case "sourcing": return [["Tons into the program", `${num(d.tons)} t`], ["Projected cases", num(d.projectedCases), "Projected cases"]];
    case "farming": return [["Estate tons", `${num((f.estateAcres ?? 0) * (f.estateTonsPerAcre ?? 0))} t`], ["Acre-months", num(d.acreMonths)]];
    case "receiving": return [["Tons to receive", `${num(d.tons)} t`], ["Weigh tags (about 2.5 t a load)", num(Math.ceil(d.tons / 2.5), 0)]];
    case "crush_press": return [["Gallons at press", num(d.gallonsPressed, 0)], ["Cases if bottled now", num(d.gallonsPerCase ? d.gallonsPressed / d.gallonsPerCase : 0, 0)]];
    case "fermentation": return [["Gallons fermenting", num(d.gallonsPressed, 0)]];
    case "aging": return [["Gallon-months", num(d.gallonMonths, 0), "Gallon-months"], ["Gallons after aging", num(d.gallonsAged, 0)], ["Barrels (59 gal)", f.agingRoute === "barrel" ? num(d.barrels, 0) : "None"]];
    case "blending": return [["Gallons after blending", num(d.gallonsBlended, 0)]];
    case "bottling": return [
      ["Projected cases", num(d.projectedCases), "Projected cases"],
      ["Against target", f.targetCases ? `${d.projectedCases >= f.targetCases ? "+" : ""}${num(((d.projectedCases - f.targetCases) / f.targetCases) * 100)}%` : "—"],
      ["Bottles", num(d.bottles, 0)],
    ];
    case "warehouse": return [["Case-months", num(d.caseMonths, 0)]];
  }
}

function ProcessCard({ ctx, s }: { ctx: Ctx; s: StageView }) {
  const { b } = ctx;
  // a template carries no cost tolerance: each program sets its own
  const defs = STAGE_FIELDS[s.stage].filter(d => (!d.onlyIf || b.fields.fruitSource === d.onlyIf) && !(d.key === "tolerancePct" && !b.tolerance));
  const save = (key: FieldKey, value: unknown) => ctx.write("", "PATCH", { changes: { [key]: value } });
  // in a re-plan, a field that differs from the approved plan carries a dot with the approved value
  const was = b.approval?.state.startsWith("replan") ? b.approval.approved?.fields : undefined;
  const changedOf = (d: FieldDef, label: (v: unknown) => string) => (was && was[d.key] !== b.fields[d.key] ? label(was[d.key]) : undefined);
  return (
    <Card title={<span className="ppt-label">Process <i>quantities and drivers</i></span>} headingId={`proc-${s.stage}`}
      description="What goes in, what comes out, and how long it takes. Drivers spread pooled costs.">
      <div className="card__c fields bldfields">
        {defs.map(d => {
          if (d.kind === "source" || d.kind === "route") {
            const opts = d.kind === "source" ? [["purchased", "Purchased"], ["estate", "Estate"]] : [["none", "None"], ["stainless", "Stainless"], ["barrel", "Barrel"]];
            if (ctx.readOnly) return <ReadValue key={d.key} label={d.label} tip={d.tip} value={opts.find(o => o[0] === b.fields[d.key])?.[1] ?? ""} />;
            return <SelectField key={d.key} label={d.label} tip={d.tip} value={String(b.fields[d.key])} options={opts as [string, string][]} onSave={v => save(d.key, v)}
              changed={changedOf(d, v => opts.find(o => o[0] === v)?.[1] ?? String(v))} />;
          }
          const v = b.fields[d.key] as number | null;
          const shown = d.key === "tolerancePct" && v === null ? b.tolerance?.pct ?? 0 : v ?? 0;
          if (ctx.readOnly) return <ReadValue key={d.key} label={d.label} tip={d.tip} value={`${num(shown, 2)}${d.kind === "pct" ? "%" : ""}`} />;
          return (
            <NumberField key={d.key} label={d.label} tip={d.tip} value={shown} max={d.kind === "pct" ? 100 : undefined}
              changed={changedOf(d, v => (v === null ? `${num(b.tolerance?.pct ?? 0, 2)}% (winery default)` : `${num(Number(v), 2)}${d.kind === "pct" ? "%" : ""}`))}
              hint={d.key === "tolerancePct" && b.tolerance?.isDefault ? "The winery default." : undefined}
              onSave={n => save(d.key, n)} />
          );
        })}
        {derivedOf(b, s.stage).map(([k, v, tip]) => <div key={k} className="derived"><span>{k}{tip && <> <Tip term={tip} /></>}</span><b className="tnum">{v}</b></div>)}
        {s.stage === "aging" && b.monthsSoFar && <MonthsSoFarBlock ctx={ctx} m={b.monthsSoFar} />}
      </div>
    </Card>
  );
}

function ReadValue({ label, value, tip }: { label: string; value: string; tip?: string }) {
  return <div className="field"><span className="field__lt"><span className="field__label">{label}</span>{tip && <Tip term={tip} />}</span><b className="tnum bldread">{value}</b></div>;
}

/** A process quantity: saves on blur or Enter; a bad number is marked and nothing is sent (builder B, Invalid number). */
function NumberField({ label, value, max, hint, tip, changed, onSave }: { label: string; value: number; max?: number; hint?: string; tip?: string; changed?: string; onSave: (n: number) => Promise<unknown> }) {
  const [text, setText] = useState(String(value));
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) { setText(String(value)); setError(null); } }, [value]);
  const commit = async () => {
    const t = text.trim();
    const n = Number(t);
    if (t === "" || !Number.isFinite(n) || n < 0 || (max !== undefined && n > max)) { setError(max === 100 ? PERCENT : NUMBER); return; }
    if (n === value) { setError(null); return; }
    try { await onSave(n); setError(null); } catch (e) { if (!isNetwork(e)) setError(message(e)); }
  };
  return (
    <Field label={label} hint={hint} error={error} tip={(tip || changed) && <>{tip && <Tip term={tip} />}{changed && <ChangedDot approved={changed} />}</>}>
      <input className="input num" type="text" inputMode="decimal" value={text}
        onFocus={() => { focused.current = true; }}
        onChange={e => setText(e.target.value)}
        onBlur={() => { focused.current = false; void commit(); }}
        onKeyDown={e => { if (e.key === "Enter") void commit(); }} />
    </Field>
  );
}

function SelectField({ label, value, options, tip, changed, onSave }: { label: string; value: string; options: [string, string][]; tip?: string; changed?: string; onSave: (v: string) => Promise<unknown> }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <Field label={label} error={error} tip={(tip || changed) && <>{tip && <Tip term={tip} />}{changed && <ChangedDot approved={changed} />}</>}>
      <select className="select" value={value} onChange={e => { onSave(e.target.value).then(() => setError(null), err => setError(message(err))); }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
}

/** A number in a table cell: saves on blur or Enter, error under it. */
function CellNumber({ label, value, onSave, className, readOnly, format }: { label: string; value: number | null; onSave: (n: number) => Promise<unknown>; className?: string; readOnly: boolean; format?: (n: number) => string }) {
  const [text, setText] = useState(value === null ? "" : String(value));
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);
  const errId = useId();
  useEffect(() => { if (!focused.current) { setText(value === null ? "" : String(value)); setError(null); } }, [value]);
  if (readOnly) return <span className="tnum">{value === null ? "—" : format ? format(value) : num(value, 2)}</span>;
  const commit = async () => {
    const t = text.trim();
    const n = Number(t);
    if (t === "" || !Number.isFinite(n) || n < 0) { setError(NUMBER); return; }
    if (n === value) { setError(null); return; }
    try { await onSave(n); setError(null); } catch (e) { if (!isNetwork(e)) setError(message(e)); }
  };
  return (
    <div className="cellnum">
      <input className={`input input--cell num ${className ?? ""}`} type="text" inputMode="decimal" aria-label={label} value={text}
        aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined}
        onFocus={() => { focused.current = true; }} onChange={e => setText(e.target.value)}
        onBlur={() => { focused.current = false; void commit(); }} onKeyDown={e => { if (e.key === "Enter") void commit(); }} />
      {error && <small className="field__error" id={errId}>{error}</small>}
    </div>
  );
}

/* ---------------------------------------------------------------- rows: shared */

/** Removes a row with a 6-second Undo that puts it back where it was (builder C). */
function useRemove(ctx: Ctx, s: StageView) {
  return async (path: string, name: string, restorePath: string, restoreOf: (removed: Record<string, unknown>) => Record<string, unknown>) => {
    try {
      const res = await ctx.write(`/stages/${s.stage}/${path}`, "DELETE", null);
      if (!res) return;
      const after = res.program.revision;
      ctx.toast(`Removed "${name}".`, "default", {
        label: "Undo",
        onSelect: () => {
          ctx.write(`/stages/${s.stage}/${restorePath}`, "POST", { restore: restoreOf(res.removed ?? {}) }, { revision: after })
            .then(r => { if (r) ctx.toast("Restored."); }, e => ctx.toast(message(e), "error"));
        },
      });
    } catch (e) { ctx.toast(message(e), "error"); }
  };
}

function RemoveButton({ name, onRemove, label }: { name: string; onRemove: () => void; label?: string }) {
  return <button type="button" className="rowx" aria-label={label ?? `Remove ${name}`} onClick={onRemove}>{icons.x}</button>;
}

/* ---------------------------------------------------------------- people */

function PeopleCard({ ctx, s }: { ctx: Ctx; s: StageView }) {
  const remove = useRemove(ctx, s);
  const [focusLine, setFocusLine] = useState<string | null>(null);
  const add = async () => {
    try {
      const res = await ctx.write(`/stages/${s.stage}/labor-lines`, "POST", {});
      if (res?.lineId) setFocusLine(res.lineId);
    } catch (e) { ctx.toast(message(e), "error"); }
  };
  const activities = ctx.b.options.activities.filter(a => a.stage === s.stage);
  return (
    <Card title={<span className="ppt-label">People <i>labor</i></span>} headingId={`people-${s.stage}`}
      description={<>Hours by role at the labor pool's loaded rate. Each activity is Small, Medium or Large by {ctx.template ? "this template's base volume" : "this program's volume"}, and its hours come from the team's estimate for that size. Rates live under <Link href="/rates">Rates and pools</Link>.</>}
      actions={!ctx.readOnly && <button type="button" className="btn btn--sm" onClick={() => void add()}>{icons.plus}Add role</button>}>
      <div className="card__c">
        {s.labor.length === 0 ? <p className="muted m0">No labor in this stage yet. Add a role to plan hours.</p> : (
          <DataTable label={`People in ${STAGE_NAME[s.stage]}`}>
            <thead><tr>
              <th scope="col">Role<span className="small muted">, labor pool and size</span> <Tip term="Labor pool" /> <Tip term="Activity size" /></th>
              <th scope="col" className="r">Hours</th><th scope="col" className="r">Rate</th><th scope="col" className="r">Cost</th>
              {!ctx.readOnly && <th scope="col"><span className="sr">Remove</span></th>}
            </tr></thead>
            <tbody>
              {s.labor.map(r => (
                <PeopleRow key={r.id} ctx={ctx} s={s} r={r} activities={activities} autoFocus={focusLine === r.id} onFocused={() => setFocusLine(null)}
                  onRemove={() => void remove(`labor-lines/${r.id}`, r.role, "labor-lines", removed => removed)} />
              ))}
            </tbody>
          </DataTable>
        )}
        <div className="subtotal"><span className="muted">Labor</span><b className="tnum">{s.used ? usd0(s.laborCents) : "—"}</b></div>
      </div>
    </Card>
  );
}

function hoursSource(r: LaborRow, moved: SizeChange | undefined): ReactNode {
  if (!r.inProcess) return <span className="small bldwarn">Not in the current process. Hours kept from the approved plan.</span>;
  if (!r.activityKey || !r.size) return null;
  const nm = SIZE_NAME[r.size];
  if (moved?.hand) return <span className="small bldwarn">Volume is now in {SIZE_NAME[moved.to]}. You set {nm} by hand.</span>;
  if (r.hoursSource === "median") return <span className="small muted">Team estimate, {nm} median</span>;
  if (r.hoursSource === "band_typed") return <span className="small muted">Entered by hand, {nm}, {tons1(r.hours ?? 0)} h</span>;
  const lead = r.hoursSource === "template" ? "From template." : "Typed.";
  return <span className="small muted">{lead}{r.median === null && r.bandHours === null ? ` No team estimate for ${nm} yet.` : ""}</span>;
}

function PeopleRow({ ctx, s, r, activities, autoFocus, onFocused, onRemove }: {
  ctx: Ctx; s: StageView; r: LaborRow; activities: BuilderBody["options"]["activities"]; autoFocus: boolean; onFocused: () => void; onRemove: () => void;
}) {
  const path = `/stages/${s.stage}/labor-lines/${r.id}`;
  const [role, setRole] = useState(r.role);
  const [roleErr, setRoleErr] = useState<string | null>(null);
  const [sizing, setSizing] = useState<Size | null>(null);
  const roleRef = useRef<HTMLInputElement>(null);
  const roleFocused = useRef(false);
  useEffect(() => { if (!roleFocused.current) setRole(r.role); }, [r.role]);
  useEffect(() => { if (autoFocus) { roleRef.current?.focus(); roleRef.current?.select(); onFocused(); } }, [autoFocus, onFocused]);
  const patch = (body: Record<string, unknown>) => ctx.write(path, "PATCH", body);
  const saveRole = async () => {
    const v = role.trim();
    if (!v) { setRoleErr("Give the role a name, for example Cellar crew: topping."); return; }
    if (v === r.role) { setRoleErr(null); return; }
    try { await patch({ role: v }); setRoleErr(null); } catch (e) { if (!isNetwork(e)) setRoleErr(message(e)); }
  };
  const fail = (e: unknown) => ctx.toast(message(e), "error");
  const moved = ctx.moved.find(m => m.lineId === r.id);
  const range = sizeLine(r, ctx.template);
  const tip = r.sizeSource === "hand" ? `${range} Set by hand${r.sizeSetBy ? ` by ${r.sizeSetBy}` : ""}${r.sizeSetAt ? ` on ${day(r.sizeSetAt)}` : ""}: ${r.sizeReason ?? ""}` : range;
  const errId = useId();
  const cols = ctx.readOnly ? 4 : 5;
  return (
    <>
      <tr>
        <td>
          <div className="prow">
            {ctx.readOnly ? <b>{r.role}</b> : (
              <>
                <input ref={roleRef} className="input input--cell" aria-label="Role" value={role} maxLength={80}
                  aria-invalid={roleErr ? true : undefined} aria-describedby={roleErr ? errId : undefined}
                  onFocus={() => { roleFocused.current = true; }} onChange={e => setRole(e.target.value)}
                  onBlur={() => { roleFocused.current = false; void saveRole(); }} onKeyDown={e => { if (e.key === "Enter") void saveRole(); }} />
                {roleErr && <small className="field__error" id={errId}>{roleErr}</small>}
              </>
            )}
            <div className="prow__sel">
              {ctx.readOnly ? <span className="small muted">{r.laborPoolName}{r.activityName ? ` · ${r.activityName}` : ""}</span> : (
                <>
                  <select className="select input--cell" aria-label={`Labor pool for ${r.role}`} value={r.laborPoolId} onChange={e => void patch({ laborPoolId: e.target.value }).catch(fail)}>
                    {!ctx.b.options.laborPools.some(p => p.id === r.laborPoolId) && <option value={r.laborPoolId}>{r.laborPoolName}</option>}
                    {ctx.b.options.laborPools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select className="select input--cell" aria-label={`Activity for ${r.role}`} value={r.activityKey ?? ""} onChange={e => void patch({ activityKey: e.target.value || null }).catch(fail)}>
                    <option value="">Not linked to an activity</option>
                    {r.activityKey && !activities.some(a => a.key === r.activityKey) && <option value={r.activityKey}>{r.activityName ?? r.activityKey}</option>}
                    {activities.map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
                  </select>
                </>
              )}
            </div>
            {r.activityKey && r.size ? (
              <div className="prow__size">
                <span title={tip}><StatusChip tone={r.sizeSource === "hand" ? "info" : "neutral"} quiet={r.sizeSource !== "hand"}>{SIZE_NAME[r.size]} · {r.sizeSource === "hand" ? "set by hand" : "auto"}</StatusChip></span>
                <span className="sr">{tip}</span>
                {!ctx.readOnly && <SizeMenu r={r} onPick={z => (z === "auto" ? void patch({ size: "auto" }).catch(fail) : setSizing(z))} />}
              </div>
            ) : r.activityKey ? <span className="small muted">No sizes for this activity yet.</span> : null}
            {hoursSource(r, moved)}
            {!ctx.readOnly && r.activityKey && r.size && r.bandHours !== null && !(r.hoursSource === "band_typed" && r.hours === r.bandHours) && (
              <button type="button" className="linkbtn small" onClick={() => void patch({ useMedian: true }).then(res => { if (res) ctx.toast(`${r.role} uses the ${SIZE_NAME[r.size!]} hours.`); }, fail)}>
                Use the {SIZE_NAME[r.size]} hours<span className="sr"> for {r.role}</span>
              </button>
            )}
            {ctx.editSizes && r.activityKey && (
              <span className="small muted">
                {r.band ? `Small up to ${num(r.band.smallMax, 3)} ${r.band.unit} · Medium up to ${num(r.band.mediumMax, 3)} ${r.band.unit} · Large above ${num(r.band.mediumMax, 3)} ${r.band.unit} ` : ""}
                <button type="button" className="linkbtn small" onClick={() => ctx.editSizes!(r.activityKey!)}>Edit sizes<span className="sr"> for {r.activityName ?? r.role}</span></button>
              </span>
            )}
          </div>
        </td>
        <td className="r"><CellNumber label={`Hours for ${r.role}`} value={r.hours} readOnly={ctx.readOnly} className="w-hours" onSave={n => patch({ hours: n })} /></td>
        <td className="r tnum">{!s.used ? "—" : r.rateCents === null ? <span className="bldwarn">No rate</span> : rate(r.rateCents)}</td>
        <td className="r tnum">{!s.used ? "—" : r.noRate || r.cents === null ? <span className="bldwarn">No rate</span> : usd0(r.cents)}</td>
        {!ctx.readOnly && <td><RemoveButton name={r.role} onRemove={onRemove} /></td>}
      </tr>
      {sizing && (
        <tr><td colSpan={cols}>
          <ReasonForm label={`Why is "${r.role}" ${SIZE_NAME[sizing]}?`} placeholder="For example: extended maceration, 35 days on skins" submit={`Set ${SIZE_NAME[sizing]}`}
            required="Say why this activity is a different size."
            onCancel={() => setSizing(null)}
            onSubmit={async why => {
              const res = await patch({ size: sizing, sizeReason: why });
              if (res) { setSizing(null); ctx.toast(`${r.role} is ${SIZE_NAME[sizing]}, set by hand.`); }
            }} />
        </td></tr>
      )}
    </>
  );
}

function SizeMenu({ r, onPick }: { r: LaborRow; onPick: (z: Size | "auto") => void }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    const away = (e: MouseEvent) => { if (!menu.current?.contains(e.target as Node) && e.target !== btn.current) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const choices: { v: Size | "auto"; label: string }[] = [
    ...(["S", "M", "L"] as Size[]).filter(z => z !== r.size || r.sizeSource !== "hand").filter(z => z !== r.size).map(z => ({ v: z, label: SIZE_NAME[z] })),
    ...(r.sizeSource === "hand" ? [{ v: "auto" as const, label: "Set back to Auto" }] : []),
  ];
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); btn.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); all[(i + 1) % all.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); all[(i - 1 + all.length) % all.length]?.focus(); }
    else if (e.key === "Tab") setOpen(false);
  };
  return (
    <div className="moremenu">
      <button ref={btn} type="button" className="btn btn--sm btn--ghost" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(o => !o)}>
        Change<span className="sr"> the size of {r.role}</span>
      </button>
      {open && (
        <div className="menu" id={id} role="menu" aria-label={`Size of ${r.role}`} ref={menu} onKeyDown={onKey}>
          {choices.map(c => <button key={c.v} type="button" role="menuitem" className="menu__item" onClick={() => { setOpen(false); onPick(c.v); }}>{c.label}</button>)}
        </div>
      )}
    </div>
  );
}

/** The inline reason form for a size set by hand or a changed item cost (5 to 500 characters). */
function ReasonForm({ label, placeholder, submit, required, onSubmit, onCancel }: { label: string; placeholder: string; submit: string; required: string; onSubmit: (why: string) => Promise<void>; onCancel: () => void }) {
  const [why, setWhy] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); }, []);
  return (
    <form className="bldreason" noValidate onSubmit={e => {
      e.preventDefault();
      const v = why.trim();
      if (v.length < 5) { setError(required); input.current?.focus(); return; }
      setBusy(true);
      onSubmit(v).catch(err => setError(message(err))).finally(() => setBusy(false));
    }}>
      <Field label={label} error={error} className="bldreason__f">
        <input ref={input} className="input" maxLength={500} placeholder={placeholder} value={why} onChange={e => setWhy(e.target.value)}
          onKeyDown={e => { if (e.key === "Escape") onCancel(); }} />
      </Field>
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>{submit}</button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------- tools */

function ToolsCard({ ctx, s }: { ctx: Ctx; s: StageView }) {
  const remove = useRemove(ctx, s);
  const [byHand, setByHand] = useState(false);
  const add = async () => {
    try { await ctx.write(`/stages/${s.stage}/material-lines`, "POST", {}); } catch (e) { ctx.toast(message(e), "error"); }
  };
  const qbo = ctx.b.qboConnected;
  return (
    <Card title={<span className="ppt-label">Tools <i>materials and supplies</i></span>} headingId={`tools-${s.stage}`}
      description={qbo ? "Items come from QuickBooks with their last cost. Quantities can scale with tons, gallons, cases or bottles." : "Quantities can scale with tons, gallons, cases or bottles."}
      actions={!ctx.readOnly && <button type="button" className="btn btn--sm" onClick={() => void add()}>{icons.plus}Add item</button>}>
      <div className="card__c">
        {!qbo && (
          <div className="bldnote bldnoqbo">
            <InlineAlert variant="info" title="QuickBooks isn't connected. Pick from the item list, or add an item by hand." />
            {!ctx.readOnly && !byHand && <button type="button" className="btn btn--sm" onClick={() => setByHand(true)}>Add an item by hand</button>}
          </div>
        )}
        {!qbo && byHand && !ctx.readOnly && <HandItemForm ctx={ctx} onClose={() => setByHand(false)} />}
        {s.materials.length === 0 ? <p className="muted m0">No materials in this stage yet.</p> : (
          <DataTable label={`Tools in ${STAGE_NAME[s.stage]}`}>
            <thead><tr>
              <th scope="col">Item</th><th scope="col">Basis <Tip term="Basis" /></th><th scope="col" className="r">Qty</th><th scope="col" className="r">Total qty</th>
              <th scope="col" className="r">Unit cost</th><th scope="col" className="r">Cost</th>
              {!ctx.readOnly && <th scope="col"><span className="sr">Remove</span></th>}
            </tr></thead>
            <tbody>
              {s.materials.map(m => (
                <ToolRow key={m.id} ctx={ctx} s={s} m={m}
                  onRemove={() => void remove(`material-lines/${m.id}`, m.itemName ?? m.description, "material-lines", removed => removed)} />
              ))}
            </tbody>
          </DataTable>
        )}
        <div className="subtotal"><span className="muted">Materials</span><b className="tnum">{s.used ? usd0(s.materialCents) : "—"}</b></div>
      </div>
    </Card>
  );
}

function ToolRow({ ctx, s, m, onRemove }: { ctx: Ctx; s: StageView; m: MaterialRow; onRemove: () => void }) {
  const path = `/stages/${s.stage}/material-lines/${m.id}`;
  const [costing, setCosting] = useState<number | null>(null);
  const patch = (body: Record<string, unknown>) => ctx.write(path, "PATCH", body);
  const fail = (e: unknown) => ctx.toast(message(e), "error");
  const name = m.itemName ?? m.description;
  const cols = ctx.readOnly ? 6 : 7;
  const saveCost = async (dollars: number) => {
    const cents = Math.round(dollars * 1_000_000) / 10_000;
    // a cost different from the item's needs a reason (builder C, H4)
    if (m.itemCostCents !== null && cents !== m.itemCostCents) { setCosting(cents); return; }
    await patch({ unitCostCents: cents });
  };
  const changedTip = m.unitCostSource === "override" && m.itemCostCents !== null
    ? `Item cost ${rate(m.itemCostCents)}. Changed${m.unitCostSetBy ? ` by ${m.unitCostSetBy}` : ""}: ${m.unitCostReason ?? ""}` : null;
  return (
    <>
      <tr className={m.skipped ? "bldskip" : undefined}>
        <td>
          <div className="prow">
            {ctx.readOnly ? <b>{name}</b> : (
              <select className="select input--cell" aria-label={`Item for ${name}`} value={m.itemId ?? ""} onChange={e => { if (e.target.value) void patch({ itemId: e.target.value }).catch(fail); }}>
                {!m.itemId && <option value="">{m.description === "New item" ? "Pick an item…" : m.description}</option>}
                {ctx.b.options.items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            )}
            {m.qboId && <span className="kind">QuickBooks item: <span className="mono">{m.qboId}</span></span>}
            {!m.qboId && m.itemId && ctx.b.options.items.find(i => i.id === m.itemId)?.source === "manual" && <span className="kind">Added by hand</span>}
            {m.skipped && <span className="small muted">Only for {m.onlyForSource === "estate" ? "estate" : "purchased"} fruit</span>}
          </div>
        </td>
        <td>
          {ctx.readOnly ? BASIS_LABEL[m.basis] : (
            <select className="select input--cell" aria-label={`Quantity basis for ${name}`} value={m.basis} onChange={e => void patch({ basis: e.target.value }).catch(fail)}>
              {(Object.keys(BASIS_LABEL) as Basis[]).map(k => <option key={k} value={k}>{BASIS_LABEL[k]}</option>)}
            </select>
          )}
        </td>
        <td className="r"><CellNumber label={`Quantity for ${name}`} value={m.qtyPerBasis} readOnly={ctx.readOnly} className="w-qty" onSave={n => patch({ qtyPerBasis: n })} /></td>
        <td className="r tnum">{num(m.totalQty, m.totalQty < 100 ? 1 : 0)}</td>
        <td className="r">
          <CellNumber label={`Unit cost for ${name}, in dollars`} value={m.unitCostCents / 100} readOnly={ctx.readOnly} className="w-cost" format={n => rate(n * 100)} onSave={saveCost} />
          {changedTip && (
            <div className="bldchanged">
              <span title={changedTip}><StatusChip tone="info">Changed</StatusChip></span><span className="sr">{changedTip}</span>
              {!ctx.readOnly && <button type="button" className="linkbtn small" onClick={() => void patch({ useItemCost: true }).catch(fail)}>Use item cost</button>}
            </div>
          )}
        </td>
        <td className="r tnum">{s.used ? usd0(m.cents) : "—"}</td>
        {!ctx.readOnly && <td><RemoveButton name={name} onRemove={onRemove} /></td>}
      </tr>
      {costing !== null && (
        <tr><td colSpan={cols}>
          <ReasonForm label={`Why does ${name} cost ${rate(costing)} instead of ${rate(m.itemCostCents ?? 0)}?`} placeholder="For example: new supplier quote for this vintage" submit="Save cost"
            required="Say why the cost differs from the item's."
            onCancel={() => setCosting(null)}
            onSubmit={async why => { const res = await patch({ unitCostCents: costing, unitCostReason: why }); if (res) setCosting(null); }} />
        </td></tr>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- pools */

function PoolsCard({ ctx, s }: { ctx: Ctx; s: StageView }) {
  const remove = useRemove(ctx, s);
  const [pick, setPick] = useState("");
  const [error, setError] = useState<string | null>(null);
  const offered = ctx.b.options.pools.filter(p => !s.pools.some(o => o.costPoolId === p.id));
  const add = async () => {
    if (!pick) { setError("Choose a pool to add."); return; }
    try { const res = await ctx.write(`/stages/${s.stage}/pools`, "POST", { costPoolId: pick }); if (res) { setPick(""); setError(null); } } catch (e) { setError(message(e)); }
  };
  const share = s.totalCents && s.poolCents ? ` ${Math.round((s.poolCents / s.totalCents) * 100)}% of this stage.` : "";
  return (
    <Card title={<span className="ppt-label">Pools <i>facility, barrels, equipment</i></span>} headingId={`pools-${s.stage}`}
      description={<>Shared costs spread by each pool's driver, at the rate in force in the months this stage runs.{share} Rates live under <Link href="/rates">Rates and pools</Link>.</>}
      actions={!ctx.readOnly && offered.length > 0 && (
        <div className="assign">
          <label className="sr" htmlFor={`addpool-${s.stage}`}>Pool to add</label>
          <select id={`addpool-${s.stage}`} className="select input--cell" value={pick} aria-invalid={error ? true : undefined} onChange={e => { setPick(e.target.value); setError(null); }}>
            <option value="">Add a pool…</option>
            {offered.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" className="btn btn--sm" onClick={() => void add()}>{icons.plus}Add</button>
        </div>
      )}>
      <div className="card__c">
        {error && <p className="field__error m0" role="alert">{error}</p>}
        {s.pools.length === 0 ? <p className="muted m0">No shared pools on this stage. Add one if this work uses equipment, space or energy shared with other wines.</p> : (
          <>
            <DataTable label={`Pools in ${STAGE_NAME[s.stage]}`}>
              <thead><tr>
                <th scope="col">Pool</th><th scope="col">Driver</th><th scope="col" className="r">Driver qty <Tip term="Driver qty" /></th>
                <th scope="col" className="r">Rate</th><th scope="col" className="r">Cost</th>
                {!ctx.readOnly && <th scope="col"><span className="sr">Remove</span></th>}
              </tr></thead>
              <tbody>
                {s.pools.map(o => (
                  <tr key={o.id}>
                    <td><b>{o.name}</b></td>
                    <td>{o.driverLabel}</td>
                    <td className="r tnum">{o.driverQty === null ? "—" : num(o.driverQty, o.driverQty < 100 ? 1 : 0)}</td>
                    <td className="r tnum">{!s.used ? "—" : o.rateCents === null ? <span className="bldwarn">No rate</span> : rate(o.rateCents)}</td>
                    <td className="r tnum">{!s.used ? "—" : o.noRate || o.cents === null ? <span className="bldwarn">No rate</span> : usd0(o.cents)}</td>
                    {!ctx.readOnly && <td><RemoveButton name={o.name} label={`Remove ${o.name} from this stage`}
                      onRemove={() => void remove(`pools/${o.costPoolId}`, o.name, "pools", removed => ({ costPoolId: removed.costPoolId }))} /></td>}
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <div className="subtotal"><span className="muted">Pools</span><b className="tnum">{s.used ? usd0(s.poolCents) : "—"}</b></div>
          </>
        )}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- rail */

function Rail({ b, onReprice }: { b: BuilderBody; onReprice?: () => void }) {
  const r = b.rail;
  const a = b.approval;
  // in production with no re-plan open, today's rates may price the plan differently from the approved snapshot (H10)
  const today = a && a.state === "approved" && a.approved && a.todayCostPerCaseCents !== null && a.todayCostPerCaseCents !== a.approved.costPerCaseCents ? a.todayCostPerCaseCents : null;
  const cases = r.projectedCases;
  const target = b.fields.targetCases;
  const costed = r.notCosted.length === 0;
  const ranked = b.stages.filter(s => s.used && s.railTotalCents > 0).sort((a, c) => c.railTotalCents - a.railTotalCents);
  const slots = ranked.slice(0, 5).map(s => ({ name: STAGE_NAME[s.stage], total: s.railTotalCents, share: s.railShareCents }));
  if (ranked.length > 5) {
    const rest = ranked.slice(5);
    slots.push({ name: "Other stages", total: rest.reduce((a, s) => a + s.railTotalCents, 0), share: rest.some(s => s.railShareCents === null) ? null : rest.reduce((a, s) => a + (s.railShareCents ?? 0), 0) });
  }
  const sum = slots.reduce((a, x) => a + x.total, 0) || 1;
  const pct = (t: number) => Math.round((t / sum) * 100);
  const lockedStages = r.closedThrough !== null && b.stages.some(s => s.used && s.months.some(m => m <= r.closedThrough!));
  const withActuals = b.stages.filter(s => s.used && s.variance);
  const tol = b.tolerance?.pct ?? 0;
  return (
    <aside className="rail" aria-label="Program cost summary">
      <Card title={<>Plan cost per case <Tip term="Plan cost per case" /></>} headingId="rail-cost"
        description={`${num(cases)} projected cases${target ? `, ${cases >= target ? "+" : ""}${num(((cases - target) / target) * 100)}% against target` : ""}`}>
        <div className="card__c">
          {!costed ? (
            <>
              <div className="bigcost">Not costed yet</div>
              <p className="small bldwarn">
                {r.notCosted.length === 1 ? <>Not costed yet. Set a rate for {r.notCosted[0]!.name} in <Link href="/rates">Rates and pools</Link>.</>
                  : <>Not costed yet. Set rates in <Link href="/rates">Rates and pools</Link>.</>}
              </p>
            </>
          ) : r.costPerCaseCents === null ? (
            <>
              <div className="bigcost">—</div>
              <p className="small muted">Projected cases are 0, so there's no cost per case. Check tons, yield and losses.</p>
            </>
          ) : (
            <>
              <div className="bigcost tnum" aria-live="polite">{usd(r.costPerCaseCents)}</div>
              <div className="small muted tnum">{usd(r.costPerBottleCents ?? 0)} a bottle · {usd0(r.totalCents ?? 0)} total</div>
              {slots.length > 0 && (
                <>
                  <div className="bar bar--rail" role="img" aria-label={`Cost per case by stage: ${slots.map(x => `${x.name} ${pct(x.total)}%`).join(", ")}`}>
                    {slots.map((x, i) => <i key={x.name} className={`seg seg--${i + 1}`} style={widthOf(pct(x.total))} />)}
                  </div>
                  <div className="legend">
                    {slots.map((x, i) => (
                      <div key={x.name}><i className={`seg seg--${i + 1}`} /><span>{x.name}</span><span className="tnum muted">{pct(x.total)}%</span><span className="tnum">{x.share === null ? "—" : usd(x.share)}</span></div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
          {today !== null && a?.approved && (
            <div className="bldrates">
              <p className="small m0">Approved at {usd(a.approved.costPerCaseCents)} a case. Today's rates make it {usd(today)}. The approved plan keeps its rates until a re-plan is approved.</p>
              {onReprice && <button type="button" className="btn btn--sm" onClick={onReprice}>Use today's rates</button>}
            </div>
          )}
          {lockedStages && <p className="small muted bldnote">Months through {monthName(r.closedThrough!)} use the posted rates. Rate changes apply from {monthName(r.openMonth ?? "")}.</p>}
        </div>
      </Card>
      {/* template mode has no actuals rail (builder B, Template mode) */}
      {!b.template && <Card title={<>Actual against plan <Tip term="Actual against plan" /></>} headingId="rail-actual"
        description={r.hasActuals ? `${usd0(r.actualCents)} spent so far against ${usd0(r.planToDateCents)} planned to date. Band is ±${num(tol)}%.` : undefined}>
        <div className="card__c tol">
          {!r.hasActuals || !withActuals.length ? <span className="muted small">No actuals yet. They arrive with the first monthly close after work starts.</span>
            : withActuals.map(s => {
              const v = s.variance!;
              const pos = v.pct === null ? (v.deltaCents > 0 ? 97 : 3) : Math.max(3, Math.min(97, 50 + (v.pct / (tol * 3)) * 50));
              return (
                <div key={s.stage} className="tolrow">
                  <span>{STAGE_NAME[s.stage]}</span>
                  <StageVariance s={s} />
                  <div className="tolbar" aria-hidden="true"><span className="band" /><span className="mid" /><span className={`dot dot--${v.state}`} style={leftOf(pos)} /></div>
                  {v.pct === null && <span className="small muted tolrow__n">Actual {usd0(s.actualCents)} against $0 planned to date.</span>}
                </div>
              );
            })}
        </div>
      </Card>}
    </aside>
  );
}

/* ---------------------------------------------------------------- template mode */

/**
 * Template mode (builder B, Template mode; templates.md C "Build a draft"): a draft template's plan in
 * the builder, priced in its "Priced in" company as if harvested at the next harvest. No program
 * picker, approval, actuals rail or Today marker. Save template freezes it; a saved template opens
 * read only.
 */
function TemplateBuilder({ b, t, ctx, stage, select, status, statusText, conflict, onSave }: {
  b: BuilderBody; t: TemplateInfo; ctx: Ctx; stage: StageView; select: (s: Stage) => void;
  status: "idle" | "saving" | "saved" | "failed"; statusText: string; conflict: string | null; onSave: () => Promise<unknown>;
}) {
  const f = b.fields;
  const [saving, setSaving] = useState(false);
  // the band editor opens inline under the banner (mockup v26 and v31)
  const [bandKey, setBandKey] = useState<string | null>(null);
  const draft = t.status === "draft" && !t.archived;
  const editable = draft && !b.program.readOnly;
  const summary = [
    WINE[b.program.wineType] ?? b.program.wineType,
    f.agingRoute === "none" ? "No aging" : `${ROUTE[f.agingRoute]}, ${num(Math.max(1, Math.round(f.agingMonths)))} mo`,
    `${f.fruitSource === "estate" ? "Estate" : "Purchased"} fruit`,
    `base size ${tons1(f.plannedTons)} tons, ${num(f.targetCases, 0)} cases`,
    t.showPricedIn && t.pricingCompanyName && `priced in ${t.pricingCompanyName}`,
  ].filter(Boolean).join(" · ");
  return (
    <PageShell>
      <PageHeader serif title={t.name} subtitle={summary} breadcrumbs={[{ label: "Templates", href: "/templates" }, { label: t.name }]}
        actions={editable && <>
          <span className={`small${status === "failed" ? " bldfail" : " muted"}`} role="status">{statusText}</span>
          {/* busy while it saves, so a second click can't send it twice */}
          <button type="button" className="btn btn--primary" disabled={saving} aria-busy={saving || undefined}
            onClick={() => { setSaving(true); void onSave().finally(() => setSaving(false)); }}>{saving ? "Saving…" : "Save template"}</button>
        </>} />
      <div className="bld">
        {!draft ? <InlineAlert variant="info" title="This template is saved. Duplicate it on Templates to change it."><Link href="/templates">Go to Templates</Link></InlineAlert>
          : (
            <InlineAlert variant="info" title={`Template: ${t.name}. Draft.`}>Programs don't change when you edit this.</InlineAlert>
          )}
        {draft && !t.canPrice && <InlineAlert variant="info" title="You can only price templates in your companies." />}
        {draft && t.canPrice && b.program.readOnly && <InlineAlert variant="info" title="You can see this template. Owners, controllers and winemakers change it." />}
        {conflict && <InlineAlert variant="warning" title={conflict} />}
        {editable && bandKey && <BandEditor key={bandKey} activityKey={bandKey} toast={ctx.toast} onClose={() => setBandKey(null)} onSaved={() => void ctx.refresh()} />}
        <TimelineCard b={b} />
        <div className="builder">
          <StageTabs b={b} current={stage.stage} onSelect={select} />
          <StagePanel key={stage.stage} ctx={{ ...ctx, readOnly: !editable, editSizes: editable ? k => { setBandKey(k); window.scrollTo({ top: 0 }); } : undefined }} s={stage} />
          <Rail b={b} />
        </div>
      </div>
    </PageShell>
  );
}

/* ---------------------------------------------------------------- loading */

function BuilderSkeleton() {
  return (
    <div className="bld" aria-busy="true" aria-label="Loading the program">
      <div className="card bldskel bldskel--tl"><i /><i /></div>
      <div className="builder">
        <div className="card bldskel">{[0, 1, 2, 3, 4, 5].map(i => <i key={i} />)}</div>
        <div className="card bldskel bldskel--panel"><i /><i /><i /></div>
        <div className="card bldskel"><i /><i /></div>
      </div>
    </div>
  );
}
